import React from "react";
import Payment from "../types/Payment";
import {formatNumber} from "../../common/utils/number";
import useTranslate from "../../core/languages/hooks/useTranslate";
import moment from "moment";
import {LuxonLogoPortrait} from "../icons/LuxonLogoPortrait";
import CardBrandLogo from "./CardBrandLogo";
import useCurrencyGet from "../hooks/useCurrencyGet";

interface Props {
  payment: Payment;
  currencySymbol?: string;
}

const PaymentRow: React.FC<Props> = ({payment, currencySymbol}) => {
  const t = useTranslate();
  const isRefund = payment.type !== 1;
  const date = payment.paymentAt ? moment(payment.paymentAt) : null;
  const dateText = date ? date.format('DD/MM/YYYY · HH:mm') : t('bookOk.paymentDateUnknown');
  
  const hasSecondaryCurrency = Boolean(payment.originalCurrency && payment.currency && payment.originalCurrency !== payment.currency);
  
  // Fetch both currencies (hooks must be called unconditionally)
  const {data: originalCurrencyData} = useCurrencyGet(payment.originalCurrency);
  const {data: currencyData} = useCurrencyGet(payment.currency);
  
  // When there's currency conversion: show originalCurrency/originalAmount as primary
  const primaryAmount = payment.originalAmount ?? payment.amount ?? 0;
  const primaryCurrencySymbol = hasSecondaryCurrency ? originalCurrencyData?.symbol : currencySymbol;
  
  const secondaryAmount = hasSecondaryCurrency ? payment.amount ?? null : null;
  const secondaryCurrencySymbol = hasSecondaryCurrency ? currencyData?.symbol : null;
  
  const absAmount = formatNumber(Math.abs(Number(primaryAmount || 0)));
  const secondaryAbsAmount = secondaryAmount !== null ? formatNumber(Math.abs(Number(secondaryAmount || 0))) : null;

  return (
    <div className="flex items-center gap-4 px-4 py-3 border-b border-neutral-100 dark:border-neutral-800 last:border-b-0">

      {/* Logo */}
      <div className="flex-shrink-0 w-11 flex items-center justify-center">
        {payment.tpv === 0 && <CardBrandLogo brand={payment.paymentCard?.brand} />}
        {payment.tpv === 1 && <LuxonLogoPortrait size={44} height={44} />}
      </div>

      {/* Texto: título + fecha */}
      <div className="flex-1 min-w-0">
        <div className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5">{dateText}</div>
      </div>

      {/* Importe */}
      <div className={`flex flex-col items-end flex-shrink-0 ${isRefund ? 'text-red-500' : 'text-green-600'}`}>
        <div className="text-sm font-semibold">
          {isRefund ? '−' : '+'}{absAmount} {primaryCurrencySymbol}
        </div>
        {secondaryAbsAmount !== null && secondaryAmount !== null && secondaryCurrencySymbol && (
          <div className="text-[11px] leading-tight text-neutral-500 dark:text-neutral-400">
            {secondaryAbsAmount} {secondaryCurrencySymbol}
          </div>
        )}
      </div>

    </div>
  );
};

export default PaymentRow;
